import React, { useState, useEffect, useCallback } from "react";
import { Grid, Button, Alert, Stack, Box } from "@mui/material";
import { TextField, Checkboxes, DateTimePicker, makeValidate, makeRequired } from "mui-rff";
import { format, isValid, parse } from "date-fns";
import moment from "moment-timezone";
import DateFnsUtils from "@date-io/date-fns";
import * as Yup from "yup";
import { Form } from "react-final-form";
import AuthHeader from "../../services/AuthHeader";
import Axios from "axios";
import { DATABASE_URL, SECURITY_FLAG_LIMIT, USER_ROLES } from "../../appConstants";
import { useParams, useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import { useCEFRconverter } from "../../hooks/useCEFRconverter";
import { useLoadingContext } from "../../hooks/useLoadingContext";
import { useAuthContext } from "../../hooks/useAuthContext";
import { Loader } from "../../components/common";

import "./Candidate.css";

const isSecuritySuspended = (candidate) => (
  candidate && (Number(candidate.status) === 0 || Number(candidate.flag || 0) >= SECURITY_FLAG_LIMIT)
);

const UpdateCandidate = () => {
  const { id } = useParams();
  const [candidateDetails, setCandidateDetails] = useState({});
  const [message, setMessage] = useState("");
  const [editMode, setEditMode] = useState(null);
  const navigate = useNavigate();
  const { isLoading, startLoading, stopLoading } = useLoadingContext();
  const { user } = useAuthContext();
  const isCompleted = Number(candidateDetails.completed_test) === 1;
  const securitySuspended = isSecuritySuspended(candidateDetails);
  const isAdministrator = Number(user.role) === USER_ROLES.Administrator;

  const parseStoredDateTime = (input, timezone) => {
    if (!input) {
      return null;
    }

    const normalized = String(input).replace("T", " ").replace("Z", "").slice(0, 19);
    const wallClock = moment.utc(normalized, "YYYY-MM-DD HH:mm:ss").tz(timezone || "UTC").format("YYYY-MM-DD HH:mm:ss");
    const parsed = parse(wallClock, "yyyy-MM-dd HH:mm:ss", new Date());
    return isValid(parsed) ? parsed : null;
  };

  useEffect(() => {
    fetchCandidateDetails(id);
  }, [id]);

  const fetchCandidateDetails = async (candidateId) => {
    startLoading();
    try {
      const response = await Axios.get(`${DATABASE_URL}/candidate/${candidateId}`, {
        headers: AuthHeader(),
      });
      const candidate = response.data.candidate[0];
      candidate.grade = determineGrade(candidate);

      if (candidate.test_date) {
        candidate.test_date = parseStoredDateTime(candidate.test_date, candidate.time_zone);
      }

      setCandidateDetails(candidate);
    } catch (error) {
      console.error('Error fetching candidate details:', error);
    } finally {
      stopLoading();
    }
  };

  const determineGrade = (candidate) => {
    if (isSecuritySuspended(candidate)) {
      return "Suspended";
    }
    if (!candidate.grade && candidate.completed_test) {
      return "Waiting for Grading";
    }
    if (!candidate.completed_test) {
      return "Waiting for Test";
    }
    return useCEFRconverter(candidate.grade);
  };

  const formatTestDate = (date, timezone) => {
    const wallClock = format(new Date(date), "yyyy-MM-dd HH:mm:ss");
    return moment.tz(wallClock, "YYYY-MM-DD HH:mm:ss", timezone).utc().format("YYYY-MM-DD HH:mm:ss");
  };

  const enableEdit = () => {
    setEditMode("full");
  };

  const enableNameCorrection = () => {
    setEditMode("name");
  };

  const schema = Yup.object().shape({
    first_name: Yup.string().required("First Name is required"),
    last_name: Yup.string().required("Last Name is required"),
    email: Yup.string().email().required("Email is required"),
    government_id: Yup.string().required("Government ID is required"),
    change_pass: Yup.boolean(),
    new_pass: Yup.string().when("change_pass", {
      is: true,
      then: Yup.string().min(8, "Minimum of 8 characters in a password").max(24, "Maximum of 24 characters in a password").required("Must enter new password"),
    }),
    retype_pass: Yup.string().when("change_pass", {
      is: true,
      then: Yup.string().required("Must retype new password").oneOf([Yup.ref("new_pass"), null], "Passwords must match"),
    }),
  });

  const nameCorrectionSchema = Yup.object().shape({
    first_name: Yup.string().required("First Name is required"),
    last_name: Yup.string().required("Last Name is required"),
  });

  const validate = makeValidate(editMode === "name" ? nameCorrectionSchema : schema);
  const required = makeRequired(schema);

  const handleDownloadCertificate = useCallback((id) => {
    navigate(`/candidate/certificate/${id}`, { candidateDetails });
  }, [candidateDetails, navigate]);

  const onSubmit = async (values) => {
    startLoading();
    const isNameCorrection = editMode === "name";
    setEditMode(null);

    try {
      const payload = isNameCorrection
        ? {
            first_name: values.first_name,
            last_name: values.last_name,
          }
        : {
            ...values,
            password: values.change_pass ? values.new_pass : values.password,
            test_date: formatTestDate(values.test_date, candidateDetails.time_zone),
            time_zone: candidateDetails.time_zone,
          };

      await Axios.put(`${DATABASE_URL}/candidate/${candidateDetails.id}`, payload, { headers: AuthHeader() });
      setMessage(isNameCorrection ? "Candidate name corrected!" : "Candidate Updated!");

      await fetchCandidateDetails(candidateDetails.id);
    } catch (error) {
      const errorMessage = (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.error
      ) || "Something went wrong!";

      setMessage(errorMessage);
      console.error('Error updating candidate:', error);
    } finally {
      stopLoading();
    }
  };

  const onCancel = () => {
    navigate("/candidate/manage-candidate");
  };

  const renderFormFields = () => {
    return [
      { label: "First Name", name: "first_name", type: "text", required: required.first_name },
      { label: "Last Name", name: "last_name", type: "text", required: required.last_name },
      { label: "Email", name: "email", type: "email", required: required.email },
      { label: "Government ID", name: "government_id", type: "text", required: required.government_id },
      { label: "Security Flags", name: "flag", type: "text", disabled: true },
      { label: "Password", name: "new_pass", type: "password", required: required.new_pass },
      { label: "Re-enter Password", name: "retype_pass", type: "password", required: required.retype_pass },
      {
        component: (
          <Box className="candidate-image" sx={{ width: "100%", height: 200, border: "1px solid #b8b8b8", borderRadius: "4px" }}>
            <img src={candidateDetails.image_string} alt="candidate" />
          </Box>
        ),
      },
      {
        component: (
          <Box sx={{ width: "100%", height: 200, border: "1px solid #b8b8b8", borderRadius: "4px", padding: 2 }}>
            {!isSecuritySuspended(candidateDetails) && candidateDetails.completed_test && candidateDetails.grade !== 'Waiting for Grading' && candidateDetails.grade !== 'Waiting for Test' ? (
              <center onClick={() => handleDownloadCertificate(id)}>
                Click on the Icon to download the certificate
                <div>
                  <img src="/images/pdf-logo.png" alt="logo" />
                </div>
              </center>
            ) : isSecuritySuspended(candidateDetails) ? (
              <center>Certificate unavailable because test is suspended</center>
            ) : (
              <center>Certificate will be ready when grading is finished</center>
            )}
          </Box>
        ),
      },
      { component: <DateTimePicker label="Test Date" name="test_date" disabled={editMode !== "full"} dateFnsUtils={DateFnsUtils} initialValue={candidateDetails.test_date ? new Date(candidateDetails.test_date) : null} /> },
      { label: "Overall Score", name: "grade", type: "text", disabled: true },
      { label: "CEFR", name: "cefr", type: "text", disabled: true },
      { component: <Checkboxes name="change_pass" required={required.change_pass} disabled={editMode !== "full"} data={{ label: "Set new password?", value: false }} /> },
    ].map((field, index) => {
      const isNameField = field.name === "first_name" || field.name === "last_name";
      const isFieldDisabled = field.disabled || !editMode || (editMode === "name" && !isNameField);

      return (
        <Grid item xs={3} key={index}>
          {field.component || <TextField {...field} disabled={isFieldDisabled} />}
        </Grid>
      );
    });
  };

  return (
    <div>
      <Stack spacing={2}>
        <div className="title-with-button">
          <h1>Candidate Details</h1>
          {isCompleted ? (
            isAdministrator && (
              <Button
                variant="outlined"
                size="large"
                startIcon={<EditIcon />}
                onClick={enableNameCorrection}
                disabled={Boolean(editMode) || securitySuspended || !candidateDetails.id}
              >
                Correct Name
              </Button>
            )
          ) : (
            <Button
              variant="outlined"
              size="large"
              startIcon={<EditIcon />}
              onClick={enableEdit}
              disabled={Boolean(editMode) || securitySuspended || !candidateDetails.id}
            >
              Enable Edit
            </Button>
          )}
        </div>
        {isLoading ? (
          <div className="loader-display">
            <Loader />
          </div>
        ) : (
          <>
            {message && <Alert severity="success">{message}</Alert>}
            <Form
              onSubmit={onSubmit}
              validate={validate}
              initialValues={candidateDetails}
              render={({ hasSubmitErrors, handleSubmit, submitting }) => (
                <form onSubmit={handleSubmit} className="add-user-form">
                  <Grid container direction="row" spacing={4}>
                    {renderFormFields()}
                  </Grid>
                  <Grid container spacing={4} className="form-button-group">
                    <Grid item>
                      <Button variant="contained" color="primary" type="submit" size="large" disabled={submitting || !editMode}>
                        {editMode === "name" ? "Save Name" : "Update"}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button variant="outlined" color="primary" size="large" onClick={onCancel} disabled={submitting}>
                        CANCEL
                      </Button>
                    </Grid>
                  </Grid>
                  {hasSubmitErrors && <Alert severity="error">error</Alert>}
                </form>
              )}
            />
          </>
        )}
      </Stack>
    </div>
  );
};

export default UpdateCandidate;
